/**
 * Closure / `within` module — the distance & reachability substrate the v0.10
 * selector core (`>>`, `<>`, cones) evaluates over. (Design §1, plan P0.2.)
 *
 * ## The vertex model
 *
 * The edge universe is TWO structurally-disjoint subgraphs:
 *   - **call** edges collapse to `module → module` (`fromModulePath` →
 *     `toModulePath`; the interface refinement is an endpoint-MATCH concern the
 *     evaluator resolves, not a distance concern);
 *   - **do** edges are `process → process` (`fromProcessId` → `toProcessId`).
 *
 * Vertices are string keys, prefixed `m:` / `p:`, so a module and a process can
 * never share a key — which is exactly why **call and do never compose across
 * kinds** (design OQ 12): no path can step from a call hop onto a do hop because
 * they share no vertex. Both live in one adjacency; the disjointness is
 * structural, not a special case.
 *
 * ## Distances
 *
 * Unweighted (every edge = 1 hop), multi-source BFS, memoized per anchor-set.
 *   - directed (`>>`): `dist(X,u) + 1 + dist(v,Y) ≤ N` for an edge `u → v`;
 *   - undirected (`<>`): SYMMETRIZED to include the reversed base case
 *     (`A <> B within 1` must include the edge `B → A`) —
 *     `min(dU(X,u)+dU(v,Y), dU(X,v)+dU(u,Y)) + 1 ≤ N`, distances direction-blind.
 * `N = Infinity` is the unbounded cone (`* >> Payments`).
 *
 * ## Performance
 *
 * Vertices are interned to dense integer ids and adjacency/BFS run over typed
 * arrays, so a full structure build + query clears the P0.2 budget (<5ms warm
 * re-eval at 10k edges — see `bench/closure.mjs`) WITHOUT needing per-dirty-SCC
 * incremental patching. Tarjan SCCs are still exposed for the delta-engine
 * invalidation contract (`dirtySccIds`) and cycle handling.
 *
 * ## Cycles
 *
 * `distancesFrom` anchors are distance 0 (the within formula's base). Cone
 * reachability (`reach`) uses ≥1-hop semantics, so a node reaches itself only
 * through a real cycle (design "self-reach ≥1 hop").
 */

import type { DependencyEdge, DependencyGraph } from "./graph.js";

export type ClosureMode = "directed" | "undirected";

/** Vertex key for a module (call-subgraph endpoint). */
export function moduleKey(path: readonly string[]): string {
  return `m:${path.join(".")}`;
}

/** Vertex key for a process/subprocess (do-subgraph endpoint). */
export function processKey(id: string): string {
  return `p:${id}`;
}

/** The two endpoint keys of an edge, `u → v` (directed). */
function edgeEndpoints(e: DependencyEdge): readonly [string, string] {
  return e.kind === "do"
    ? [processKey(e.fromProcessId), processKey(e.toProcessId)]
    : [moduleKey(e.fromModulePath), moduleKey(e.toModulePath)];
}

export interface QualifyOpts {
  /** Source anchor vertex keys (`X`). */
  readonly from: ReadonlySet<string>;
  /** Sink anchor vertex keys (`Y`). */
  readonly to: ReadonlySet<string>;
  /** Hop bound `N`; `Infinity` = unbounded cone. */
  readonly within: number;
  readonly mode: ClosureMode;
}

/** A resolved shortest path — see {@link Closure.shortestPath}. */
export interface ClosurePath {
  /** Vertex keys, inclusive of both endpoints (`length - 1` hops). */
  readonly vertices: readonly string[];
  /** `edges[i]` joins `vertices[i] → vertices[i + 1]`. */
  readonly edges: readonly DependencyEdge[];
}

export interface Closure {
  /** All vertex keys in the universe (both subgraphs). */
  readonly nodeCount: number;
  hasNode(key: string): boolean;
  /**
   * Edges qualifying `X (>>|<>) Y within N`. `mode: "directed"` is `>>`,
   * `"undirected"` is the symmetrized `<>`. Returns the original edge objects,
   * in `graph.edges` order.
   */
  qualifyEdges(opts: QualifyOpts): DependencyEdge[];
  /**
   * Cone node set — vertices reachable from `anchors` (`downstream`) or that
   * reach `anchors` (`upstream`), via ≥1 hop (a node appears via itself only
   * through a cycle). Direction-blind when `mode === "undirected"`.
   */
  reach(anchors: ReadonlySet<string>, direction: "downstream" | "upstream", mode?: ClosureMode): Set<string>;
  /**
   * The same cone as {@link reach}, keeping the hop count that BFS already
   * computed instead of discarding it — the depth-graded cone the viewer paints.
   *
   * Depth is ≥1 by construction (the ≥1-hop seeding): an anchor's neighbours are
   * depth 1, and an ANCHOR appears only through a cycle, at that cycle's length.
   * So a consumer banding by depth must test anchor MEMBERSHIP separately —
   * `depth === 1` is not "the anchor", and on a cyclic model the anchor's own
   * depth is ≥2.
   */
  reachDepths(
    anchors: ReadonlySet<string>,
    direction: "downstream" | "upstream",
    mode?: ClosureMode,
  ): ReadonlyMap<string, number>;
  /**
   * Shortest directed path `from → to` — BFS plus a predecessor walk.
   *
   * `vertices` is inclusive of both endpoints, so `vertices.length - 1` is the
   * hop count and `edges[i]` is a REPRESENTATIVE edge for hop `i` (the first in
   * `graph.edges` order joining that pair, matching how `qualifyEdges` orders
   * its result). Null when either key is unknown or `to` is unreachable.
   *
   * Same ≥1-hop base as `reach`, so `shortestPath(A, A)` never returns the
   * degenerate `[A]`. Directed, it is the shortest CYCLE through `A` (null when
   * `A` sits on none); UNDIRECTED it is a there-and-back over one edge, since
   * ignoring direction makes any neighbour a way home.
   */
  shortestPath(from: string, to: string, mode?: ClosureMode): ClosurePath | null;
  /** Strongly-connected-component id of a vertex (−1 if unknown). */
  sccOf(key: string): number;
  /** SCC ids touched by a set of changed module paths — the delta-engine
   *  invalidation surface (a consumer drops memo entries over these). */
  dirtySccIds(changedModulePaths: Iterable<string>): Set<number>;
}

const INF = 0x7fffffff;

// =============================================================================
// Build
// =============================================================================

export function buildClosure(graph: DependencyGraph): Closure {
  // Intern every endpoint to a dense integer id. Isolated nodes never appear on
  // an edge, so they're irrelevant to distance/reach and are skipped.
  const id = new Map<string, number>();
  const keys: string[] = [];
  const intern = (k: string): number => {
    let i = id.get(k);
    if (i === undefined) {
      i = keys.length;
      id.set(k, i);
      keys.push(k);
    }
    return i;
  };

  // Per-edge endpoint ids (parallel to graph.edges) — computed once so the
  // per-query loop is pure integer lookups, not string joins.
  const eu = new Int32Array(graph.edges.length);
  const ev = new Int32Array(graph.edges.length);
  // Adjacency as id → id[] (fwd, rev, undirected). Dedup within a list is
  // unnecessary: BFS visits by first-seen, so parallel edges cost nothing.
  const fwd: number[][] = [];
  const rev: number[][] = [];
  const und: number[][] = [];
  const grow = (i: number) => {
    while (fwd.length <= i) {
      fwd.push([]);
      rev.push([]);
      und.push([]);
    }
  };
  for (let e = 0; e < graph.edges.length; e++) {
    const [uk, vk] = edgeEndpoints(graph.edges[e]!);
    const u = intern(uk);
    const v = intern(vk);
    eu[e] = u;
    ev[e] = v;
    grow(Math.max(u, v));
    fwd[u]!.push(v);
    rev[v]!.push(u);
    und[u]!.push(v);
    und[v]!.push(u);
  }
  const n = keys.length;

  const scc = tarjanScc(n, fwd);

  // Memoized multi-source BFS → Int32Array of distances (INF = unreachable).
  // Key = mode-tag + sorted anchor-id join; the anchor sets a policy/view
  // produces are stable across keystrokes, so warm re-eval hits the cache.
  const memo = new Map<string, Int32Array>();
  const bfs = (anchorIds: number[], adj: number[][], tag: string): Int32Array => {
    const sorted = anchorIds.slice().sort((a, b) => a - b);
    const key = `${tag}|${sorted.join(",")}`;
    const hit = memo.get(key);
    if (hit) return hit;
    const dist = new Int32Array(n).fill(INF);
    const queue = new Int32Array(n);
    let tail = 0;
    for (const a of sorted) if (dist[a] === INF) { dist[a] = 0; queue[tail++] = a; }
    for (let head = 0; head < tail; head++) {
      const cur = queue[head]!;
      const d = dist[cur]! + 1;
      for (const nxt of adj[cur]!) if (dist[nxt] === INF) { dist[nxt] = d; queue[tail++] = nxt; }
    }
    memo.set(key, dist);
    return dist;
  };

  const anchorIds = (keysIn: ReadonlySet<string>): number[] => {
    const out: number[] = [];
    for (const k of keysIn) {
      const i = id.get(k);
      if (i !== undefined) out.push(i);
    }
    return out;
  };

  // The ≥1-hop cone WITH its hop counts — one BFS, memoized per anchor set, that
  // both `reach` and `reachDepths` read. Seeding from the anchors' NEIGHBOURS
  // (not the anchors) is what makes self-reach mean "there is a cycle"; it also
  // means an anchor that IS in the map carries its cycle length, not 0.
  // Bounded: a cone map is O(V) and the viewer mints entries just by moving the
  // selection, so an unbounded memo would retain a map per module visited for
  // the graph's whole lifetime. Clearing wholesale (rather than evicting one
  // entry) keeps this a few lines; the BFS it protects is cheap to redo.
  const CONE_MEMO_MAX = 64;
  const coneMemo = new Map<string, Map<string, number>>();
  const coneDepths = (
    anchors: ReadonlySet<string>,
    direction: "downstream" | "upstream",
    mode: ClosureMode,
  ): ReadonlyMap<string, number> => {
    const ids = anchorIds(anchors).sort((a, b) => a - b);
    const key = `${mode}|${direction}|${ids.join(",")}`;
    const hit = coneMemo.get(key);
    if (hit) return hit;
    const adj = mode === "undirected" ? und : direction === "downstream" ? fwd : rev;
    const depth = new Int32Array(n).fill(INF);
    const queue: number[] = [];
    for (const a of ids) for (const nb of adj[a]!) {
      if (depth[nb] === INF) { depth[nb] = 1; queue.push(nb); }
    }
    for (let head = 0; head < queue.length; head++) {
      const cur = queue[head]!;
      const d = depth[cur]! + 1;
      for (const nxt of adj[cur]!) if (depth[nxt] === INF) { depth[nxt] = d; queue.push(nxt); }
    }
    const out = new Map<string, number>();
    for (let i = 0; i < n; i++) if (depth[i] !== INF) out.set(keys[i]!, depth[i]!);
    if (coneMemo.size >= CONE_MEMO_MAX) coneMemo.clear();
    coneMemo.set(key, out);
    return out;
  };

  // Predecessor array for a single-source BFS — the reconstruction half
  // `distancesFrom`/`bfs` deliberately drops. Same ≥1-hop seeding as the cone,
  // so `src` itself is reachable only around a cycle.
  const parentMemo = new Map<string, Int32Array>();
  const pathParents = (src: number, adj: number[][], mode: ClosureMode): Int32Array => {
    const key = `${mode}|${src}`;
    const hit = parentMemo.get(key);
    if (hit) return hit;
    const parent = new Int32Array(n).fill(-1);
    const queue: number[] = [];
    for (const nb of adj[src]!) if (parent[nb] === -1) { parent[nb] = src; queue.push(nb); }
    for (let head = 0; head < queue.length; head++) {
      const cur = queue[head]!;
      for (const nxt of adj[cur]!) if (parent[nxt] === -1) { parent[nxt] = cur; queue.push(nxt); }
    }
    if (parentMemo.size >= CONE_MEMO_MAX) parentMemo.clear();
    parentMemo.set(key, parent);
    return parent;
  };

  // First-in-`graph.edges`-order edge per ordered endpoint pair — deterministic
  // among parallel edges, and built ONCE rather than rescanned per hop (a full
  // scan per hop made `shortestPath` O(E x pathLength)).
  const edgeByPair = new Map<number, DependencyEdge>();
  for (let e = graph.edges.length - 1; e >= 0; e--) {
    // Descending, so the LOWEST index wins the slot.
    edgeByPair.set(eu[e]! * n + ev[e]!, graph.edges[e]!);
  }
  const representativeEdge = (u: number, v: number, mode: ClosureMode): DependencyEdge | undefined =>
    edgeByPair.get(u * n + v) ??
    // Orientation-blind under `undirected`: the BFS is free to have crossed the
    // edge backwards.
    (mode === "undirected" ? edgeByPair.get(v * n + u) : undefined);

  return {
    nodeCount: n,
    hasNode: (k) => id.has(k),

    qualifyEdges({ from, to, within, mode }: QualifyOpts): DependencyEdge[] {
      const out: DependencyEdge[] = [];
      const fromIds = anchorIds(from);
      const toIds = anchorIds(to);
      if (fromIds.length === 0 || toIds.length === 0) return out;
      if (mode === "directed") {
        const dX = bfs(fromIds, fwd, "F");
        const dY = bfs(toIds, rev, "R");
        for (let e = 0; e < graph.edges.length; e++) {
          const du = dX[eu[e]!]!;
          const dv = dY[ev[e]!]!;
          // Both endpoints must be REACHABLE — an unbounded `within` (Infinity)
          // would otherwise pass `INF + 1 ≤ Infinity` for an off-path edge.
          if (du !== INF && dv !== INF && du + 1 + dv <= within) out.push(graph.edges[e]!);
        }
      } else {
        const uX = bfs(fromIds, und, "U");
        const uY = bfs(toIds, und, "U");
        for (let e = 0; e < graph.edges.length; e++) {
          const u = eu[e]!;
          const v = ev[e]!;
          const a = uX[u] !== INF && uY[v] !== INF ? uX[u]! + uY[v]! : INF;
          const b = uX[v] !== INF && uY[u] !== INF ? uX[v]! + uY[u]! : INF;
          const m = a < b ? a : b;
          if (m !== INF && m + 1 <= within) out.push(graph.edges[e]!);
        }
      }
      return out;
    },

    reach(anchors, direction, mode = "directed") {
      return new Set(coneDepths(anchors, direction, mode).keys());
    },

    reachDepths(anchors, direction, mode = "directed") {
      // A COPY. The memo entry is shared by every consumer of this closure (it
      // is cached per-graph), and `ReadonlyMap` is only a compile-time promise
      // — one caller mutating what it was handed would corrupt every later
      // query. The BFS, which is the expensive half, is still memoized.
      return new Map(coneDepths(anchors, direction, mode));
    },

    shortestPath(from, to, mode = "directed") {
      const src = id.get(from);
      const dst = id.get(to);
      if (src === undefined || dst === undefined) return null;
      const adj = mode === "undirected" ? und : fwd;
      const parent = pathParents(src, adj, mode);
      if (parent[dst] === -1) return null;
      // Walk predecessors back from `dst`, then reverse. The ≥1-hop seeding
      // means `src`'s own parent is never set, so a self-path terminates on the
      // cycle's last hop rather than looping.
      // Step FIRST, then test — `dst === src` is the legitimate self-path case
      // (a cycle through `src`), and a head-tested loop would return the bare
      // `[src]` for it, claiming a 0-hop path that does not exist.
      const walk: number[] = [dst];
      let cur = dst;
      do {
        const p = parent[cur]!;
        walk.push(p);
        cur = p;
      } while (cur !== src);
      walk.reverse();
      const vertices = walk.map((i) => keys[i]!);
      const edges: DependencyEdge[] = [];
      for (let h = 0; h + 1 < walk.length; h++) {
        const e = representativeEdge(walk[h]!, walk[h + 1]!, mode);
        // Every hop came out of the adjacency the edges themselves built, so a
        // miss is impossible. Pushing nothing would silently shift every later
        // `edges[i]` off its vertex pair and mislabel the whole breadcrumb, so
        // the invariant is asserted rather than papered over.
        if (!e) throw new Error(`closure: no edge for hop ${keys[walk[h]!]} → ${keys[walk[h + 1]!]}`);
        edges.push(e);
      }
      return { vertices, edges };
    },

    sccOf: (k) => {
      const i = id.get(k);
      return i === undefined ? -1 : scc[i]!;
    },

    dirtySccIds(changedModulePaths) {
      const ids = new Set<number>();
      for (const p of changedModulePaths) {
        const key = moduleKey(Array.isArray(p) ? p : String(p).split("."));
        const i = id.get(key);
        if (i !== undefined) ids.add(scc[i]!);
      }
      return ids;
    },
  };
}

// =============================================================================
// Tarjan SCC (iterative — the graph can be deep; no recursion-depth risk)
// =============================================================================

function tarjanScc(n: number, fwd: readonly number[][]): Int32Array {
  const index = new Int32Array(n).fill(-1);
  const low = new Int32Array(n);
  const onStack = new Uint8Array(n);
  const comp = new Int32Array(n).fill(-1);
  const stack = new Int32Array(n);
  let sp = 0;
  let counter = 0;
  let compId = 0;

  // Iterative DFS: parallel frame stacks carry the node + a cursor into its
  // out-neighbour list, so we resume after recursing into a child.
  const wNode = new Int32Array(n);
  const wPos = new Int32Array(n);

  for (let root = 0; root < n; root++) {
    if (index[root] !== -1) continue;
    let wp = 0;
    wNode[wp] = root;
    wPos[wp] = 0;
    index[root] = counter;
    low[root] = counter;
    counter++;
    stack[sp++] = root;
    onStack[root] = 1;

    while (wp >= 0) {
      const node = wNode[wp]!;
      const neighbours = fwd[node]!;
      if (wPos[wp]! < neighbours.length) {
        const w = neighbours[wPos[wp]!]!;
        wPos[wp]!++;
        if (index[w] === -1) {
          index[w] = counter;
          low[w] = counter;
          counter++;
          stack[sp++] = w;
          onStack[w] = 1;
          wp++;
          wNode[wp] = w;
          wPos[wp] = 0;
        } else if (onStack[w]) {
          if (index[w]! < low[node]!) low[node] = index[w]!;
        }
      } else {
        // Done with `node` — close a component when it is a root (low == index),
        // else fold its low-link into the parent frame.
        if (low[node] === index[node]) {
          let w: number;
          do {
            w = stack[--sp]!;
            onStack[w] = 0;
            comp[w] = compId;
          } while (w !== node);
          compId++;
        }
        wp--;
        if (wp >= 0 && low[node]! < low[wNode[wp]!]!) low[wNode[wp]!] = low[node]!;
      }
    }
  }
  return comp;
}
